import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Tag } from '@weave/schema-gen-ts/dist/schemas/tag/shared/v1/models.pb';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { TagSelectionComponents, TagSelectionHooks } from '@frontend/tag-selection';
import { Chip } from '@frontend/design-system';

type Props = {
  existingTagIds?: string[];
  onClose?: () => void;
  onSelect?: (tag: Tag) => void;
  onApplyTag?: (tag: Tag) => void;
};

export const MessageInsightsTagSelector = ({ existingTagIds, onClose, onSelect, onApplyTag }: Props) => {
  const [tagSearchValue, setTagSearchValue] = useState('');
  const { navigate } = useSettingsNavigate();
  const hasOpenedRef = useRef(false);

  const {
    popoverProps: tagPopoverProps,
    open: openTagSelection,
    isOpen: tagSelectionIsOpen,
    close: closeTagSelection,
    getTriggerProps: getTagSelectionTriggerProps,
  } = TagSelectionHooks.useTagSelectionPopover({
    onClose: () => {
      closeTagSelection();
      if (hasOpenedRef.current) {
        onClose?.();
      }
    },
    onTagSelect: async (tag) => {
      closeTagSelection();
      onApplyTag?.(tag);
      onSelect?.(tag);
    },
    onTagCreate: (initName) => {
      navigate({
        to: '/tags/quick-create',
        context: {
          onCreate: (tag: Tag) => {
            onApplyTag?.(tag);
          },
          name: initName ? encodeURIComponent(initName) : '',
        },
      });
    },
    trackingIds: {},
    searchValue: tagSearchValue,
    tagIdsToExclude: existingTagIds,
  });

  useEffect(() => {
    const timeout = setTimeout(() => {
      openTagSelection();
      hasOpenedRef.current = true;
    }, 0);
    return () => clearTimeout(timeout);
  }, []);

  return (
    <span ref={getTagSelectionTriggerProps().ref}>
      <Chip.Tag
        editable
        color='gray'
        onKeyDown={(e: KeyboardEvent) => {
          if (e.key === 'Escape') {
            closeTagSelection();
            onClose?.();
          }
          if (e.key === 'Enter' && tagSelectionIsOpen) {
            e.preventDefault();
            if (tagPopoverProps.tagsList[0]) {
              closeTagSelection();
              const tag = tagPopoverProps.tagsList[0];
              onApplyTag?.(tag);
              onSelect?.(tag);
            } else {
              navigate({
                to: '/tags/quick-create',
                context: { name: tagSearchValue ? encodeURIComponent(tagSearchValue) : '' },
              });
            }
          }
        }}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          setTagSearchValue(e.currentTarget.value);
        }}
      />
      <TagSelectionComponents.TagSelectionPopover {...tagPopoverProps} returnFocus={false} initialFocus={-1} />
    </span>
  );
};
